// Drafts attached to an event. Mirrors SpeakerList / SessionList — owns its
// own fetch and falls back to whatever the parent passed in if the worker
// is unreachable. Creation is handled by the existing "+ Draft" action in
// the event-detail header (CreateDraftModal navigates to /drafts/:id), so
// this list is read-only — clicking a card opens the draft editor.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import { api } from '../../lib/api';

const CAPTION_PREVIEW_CHARS = 80;

const PLATFORM_BADGE_LABEL = {
  linkedin:  'platforms.linkedin',
  instagram: 'platforms.instagram',
  facebook:  'platforms.facebook',
  youtube:   'platforms.youtube',
};

function captionPreview(draft) {
  const source = draft.captionPl || draft.captionEn || '';
  const oneLine = source.replace(/\s+/g, ' ').trim();
  if (oneLine.length <= CAPTION_PREVIEW_CHARS) return oneLine;
  return oneLine.slice(0, CAPTION_PREVIEW_CHARS).trimEnd() + '…';
}

export function DraftList({ eventId, initial, readOnly }) {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState(initial ?? []);

  useEffect(() => {
    if (readOnly) return undefined;
    let cancelled = false;
    api.drafts
      .list({ eventId })
      .then((data) => {
        if (cancelled) return;
        setDrafts(Array.isArray(data?.drafts) ? data.drafts : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[drafts] list failed, keeping initial:', err.message);
      });
    return () => { cancelled = true; };
  }, [eventId, readOnly]);

  return (
    <section className="list-section">
      <header className="list-section__header">
        <h3 className="list-section__title">{t('events.detail.sections.drafts')}</h3>
      </header>

      {drafts.length === 0 ? (
        <p className="list-section__empty">{t('events.detail.drafts.empty')}</p>
      ) : (
        <ul className="draft-card-list">
          {drafts.map((draft) => (
            <li key={draft.id} className="draft-card">
              <div className="draft-card__head">
                <span className={`platform-badge platform-badge--${draft.platform}`}>
                  {t(PLATFORM_BADGE_LABEL[draft.platform] ?? 'platforms.linkedin')}
                </span>
                <span className={`status-badge status-badge--${draft.status}`}>
                  {t(`draft.statuses.${draft.status}`)}
                </span>
              </div>
              <p className="draft-card__caption">
                {captionPreview(draft) || (
                  <span className="draft-card__caption--empty">
                    {t('events.detail.drafts.noCaption')}
                  </span>
                )}
              </p>
              <Link className="draft-card__edit" to={`/drafts/${draft.id}`}>
                {t('common.edit')} →
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
